import { useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import { useAuth } from '../../shared/hooks/useAuth';
import { PostCard } from './components/PostCard';
import { useSavedOpportunities } from './useOpportunities';

/**
 * The current user's bookmarks page — every post they've saved, newest
 * first. Auth-required: anonymous visitors are bounced to /login with a
 * return path so they land back here after sign-in.
 */
export function SavedOpportunitiesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { posts, isLoading, isError, hasMore, isFetchingMore, loadMore } =
    useSavedOpportunities();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?next=' + encodeURIComponent('/opportunities/saved'), {
        replace: true,
      });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || !isAuthenticated) return null;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" sx={{ px: { xs: 1, sm: 0 }, mb: 1 }}>
        <IconButton
          component={RouterLink}
          to="/opportunities"
          aria-label={t('common.back')}
          edge="start"
        >
          <ArrowBackRoundedIcon />
        </IconButton>
      </Stack>

      <Box sx={{ px: { xs: 2, sm: 0 }, mb: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <BookmarkRoundedIcon sx={{ color: 'primary.main' }} />
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, letterSpacing: -0.5, fontSize: { xs: 26, md: 34 } }}
          >
            {t('opportunities.saved.title')}
          </Typography>
        </Stack>
        <Typography color="text.secondary" sx={{ fontSize: { xs: 13, md: 15 } }}>
          {t('opportunities.saved.subtitle')}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        {isError ? (
          <Alert severity="error">{t('opportunities.loadError')}</Alert>
        ) : isLoading ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={220} />
            ))}
          </Box>
        ) : posts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                alignItems: 'stretch',
                gridAutoRows: '1fr',
              }}
            >
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </Box>
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                <Button
                  onClick={() => loadMore()}
                  disabled={isFetchingMore}
                  variant="outlined"
                  sx={{ borderRadius: 999, px: 3 }}
                >
                  {isFetchingMore ? t('opportunities.loading') : t('opportunities.loadMore')}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 6, md: 10 },
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 4,
      }}
    >
      <BookmarkRoundedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography color="text.secondary">{t('opportunities.saved.empty')}</Typography>
      <Button
        component={RouterLink}
        to="/opportunities"
        variant="contained"
        sx={{ mt: 2, borderRadius: 999 }}
      >
        {t('opportunities.saved.browse')}
      </Button>
    </Box>
  );
}
